import React, { useEffect, useState } from 'react';
import { getUsersWithoutGroup, downloadUsersWithoutGroupCsv, deleteUsersWithoutGroup } from '../../services/adminService';

const AdminUsersWithoutGroup = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [deleteDate, setDeleteDate] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getUsersWithoutGroup();
      setUsers(data.users);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadUsersWithoutGroupCsv();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'no-cookbook-users.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  };

  const matchingCount = deleteDate
    ? users.filter((u) => new Date(u.created_at) < new Date(deleteDate)).length
    : 0;

  const handleDelete = async () => {
    if (!deleteDate) return;
    if (matchingCount === 0) {
      alert('No users match that date.');
      return;
    }

    const typed = window.prompt(
      `This will permanently delete ${matchingCount} account(s) registered before ${deleteDate}. This cannot be undone.\n\nType DELETE to confirm:`
    );
    if (typed !== 'DELETE') {
      if (typed !== null) alert("Didn't match — nothing was deleted.");
      return;
    }

    setDeleting(true);
    try {
      const data = await deleteUsersWithoutGroup(deleteDate);
      let message = `${data.deleted} account(s) deleted.`;
      if (data.skipped > 0) {
        message += `\n\n${data.skipped} account(s) were skipped because they have historical activity from a group they were previously part of (e.g. recipes they created before leaving): ${data.skippedEmails.join(', ')}`;
      }
      alert(message);
      setDeleteDate('');
      await fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete users');
    } finally {
      setDeleting(false);
    }
  };

  const daysAgo = (dateStr) => {
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  if (loading) return <p style={styles.status}>Loading...</p>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>👤 Registered, No Cookbook Yet</h2>
          <p style={styles.subtitle}>
            {users.length} account{users.length === 1 ? '' : 's'} that registered but never created or joined a group —
            good candidates for a "finish setting up" nudge.
          </p>
        </div>
        <button onClick={handleDownload} style={styles.downloadBtn} disabled={downloading || users.length === 0}>
          {downloading ? 'Downloading...' : '⬇ Download CSV'}
        </button>
      </div>

      {users.length > 0 && (
        <div style={styles.deleteRow}>
          <span style={styles.deleteLabel}>Delete accounts registered before</span>
          <input
            type="date"
            value={deleteDate}
            onChange={(e) => setDeleteDate(e.target.value)}
            style={styles.dateInput}
          />
          {deleteDate && (
            <span style={styles.matchCount}>
              {matchingCount} account{matchingCount === 1 ? '' : 's'} match
            </span>
          )}
          <button
            onClick={handleDelete}
            style={styles.deleteBtn}
            disabled={!deleteDate || matchingCount === 0 || deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      )}

      {error && <p style={styles.error}>{error}</p>}

      {users.length === 0 ? (
        <p style={styles.status}>Everyone who's registered has created or joined a cookbook. Nothing to show here.</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Registered</th>
                <th style={styles.th}>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={styles.td}>{u.name}</td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={styles.td}>{daysAgo(u.created_at)}</td>
                  <td style={styles.td}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString() : <span style={styles.subtle}>Never</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white', padding: '32px', borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  title: { color: '#e67e22', marginBottom: '6px' },
  subtitle: { color: '#888', fontSize: '13px', maxWidth: '440px', lineHeight: 1.5 },
  downloadBtn: {
    padding: '10px 18px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  deleteRow: {
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
    padding: '14px 16px', backgroundColor: '#fdf5f0', borderRadius: '8px', marginBottom: '20px',
  },
  deleteLabel: { fontSize: '13px', color: '#666' },
  dateInput: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' },
  matchCount: { fontSize: '12px', color: '#a05a1a', fontWeight: '600' },
  deleteBtn: {
    padding: '8px 16px', backgroundColor: '#e74c3c', color: 'white', border: 'none',
    borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginLeft: 'auto',
  },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: '12px', color: '#999', borderBottom: '2px solid #f0f0f0', textTransform: 'uppercase' },
  td: { padding: '10px 12px', fontSize: '13px', color: '#444', borderBottom: '1px solid #f5f5f5' },
  subtle: { color: '#bbb' },
  status: { textAlign: 'center', color: '#999' },
  error: { color: '#e74c3c', textAlign: 'center', marginBottom: '16px' },
};

export default AdminUsersWithoutGroup;
